import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  deleteDoc,
  query,
  orderBy,
  getDoc,
  updateDoc,
  runTransaction,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "./firebase";
import Papa from "papaparse";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCcw,
  DollarSign,
  FileText,
  Plus,
  Calendar,
  Download,
  Filter,
  X,
} from "lucide-react";

// Import our extracted components
import StatCard from "./components/sales/StatCard";
import NoteModal from "./components/sales/NoteModal";
import ActionModal from "./components/sales/ActionModal";
// import EditForm from "./components/sales/EditForm"; // Removed - now using separate page
import SalesForm from "./components/sales/SalesForm";
import SalesTable from "./components/sales/SalesTable";
import Toast from "./components/sales/Toast";
// import ScrollDebugger from "./components/ScrollDebugger";
// import useScrollStabilizer from "./hooks/useScrollStabilizer";
import { useData } from "./contexts/DataContext";
import AuditLogger from "./services/auditLogger";
import { detectChanges } from "./utils/changeDetector";

// DISABLED FOR PRODUCTION: Import testing utilities for development
// import "./utils/testRealTime";
// import "./utils/realTimeDebugger";

function Sales() {
  // Track Sales.js re-renders
  console.log('🏪 Sales.js re-render at', Date.now());

  // Use location to handle messages from edit page
  const location = useLocation();

  // Use DataContext for cached data
  const { 
    salesData, 
    productsData, 
    isFullDataLoaded,
    loadAllData,
    unloadAllData,
    error: dataError,
    refreshData
  } = useData();

  const [productFilter, setProductFilter] = useState("All");
  const [periodFilter, setPeriodFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  // Local state that mirrors salesData for immediate updates
  const [localSalesData, setLocalSalesData] = useState([]);

  // Update local sales data when context data changes
  useEffect(() => {
    setLocalSalesData(salesData || []);
  }, [salesData]);

  // Remove local flags when real data arrives from Firestore
  useEffect(() => {
    if (salesData && salesData.length > 0) {
      setLocalSalesData(prevLocal => {
        // Only update if we have real changes from Firestore
        const cleanedData = salesData.map(sale => {
          const { _localUpdate, _updateTime, ...cleanSale } = sale;
          return cleanSale;
        });
        
        // If we had local updates, merge them properly
        if (prevLocal.some(sale => sale._localUpdate)) {
          console.log('🔄 Merging real-time data with local updates');
        }
        
        return cleanedData;
      });
    }
  }, [salesData]);

  // Function to add a new sale record locally (optimistic update)
  const addSaleLocally = useCallback((newSale) => {
    setLocalSalesData(prevSales => {
      console.log('➕ Local sale added:', newSale.transactionId);
      return [{ ...newSale, _localUpdate: true, _updateTime: Date.now() }, ...prevSales];
    });
  }, []);

  // Function to update a single sale record locally
  const updateSaleLocally = useCallback((updatedSale) => {
    setLocalSalesData(prevSales => {
      const updated = prevSales.map(sale => 
        sale.transactionId === updatedSale.transactionId 
          ? { ...sale, ...updatedSale, _localUpdate: true, _updateTime: Date.now() }
          : sale
      );
      console.log('🔄 Local sale updated:', updatedSale.transactionId);
      return updated;
    });
  }, []);

  // Function to delete a sale locally
  const deleteSaleLocally = useCallback((transactionId) => {
    setLocalSalesData(prevSales => {
      const updated = prevSales.filter(sale => sale.transactionId !== transactionId);
      console.log('➖ Local sale deleted:', transactionId);
      return updated;
    });
  }, []);

  // Function to update multiple sales locally (for bulk operations)
  const updateMultipleSalesLocally = useCallback((updates, transactionIds) => {
    setLocalSalesData(prevSales => 
      prevSales.map(sale => 
        transactionIds.includes(sale.transactionId)
          ? { ...sale, ...updates, updatedAt: new Date() }
          : sale
      )
    );
  }, []);

  // Initialize scroll stabilizer to prevent scroll jumps during re-renders
  // useScrollStabilizer(true);

  // Add state for renewal pricing
  const [renewalPrices, setRenewalPrices] = useState({});
  const [renewalPricesVersion, setRenewalPricesVersion] = useState(0);

  const [sortDirection, setSortDirection] = useState({
    expirationDays: "descending",
    date: "descending",
  });
  const [duplicatingTransactionId, setDuplicatingTransactionId] =
    useState(null);
  const [renewingTransactionId, setRenewingTransactionId] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);

  // Add this function to handle sort toggling
  const toggleSort = (key) => {
    setSortDirection((prev) => ({
      ...prev,
      [key]: prev[key] === "ascending" ? "descending" : "ascending",
    }));

    setSortConfig({
      key,
      direction:
        sortDirection[key] === "ascending" ? "descending" : "ascending",
    });
  };

  // Function to handle duplication
  const handleDuplicate = async (originalTransaction) => {
    try {
      const newTrxId = await generateTransactionId();
      const duplicatedTransaction = {
        ...originalTransaction,
        transactionId: newTrxId,
        date: new Date().toISOString().split("T")[0], // Set to current date
        createdAt: new Date(),
      };

      // Overwrite detection guard
      const dupExisting = await getDoc(doc(db, "Sales", duplicatedTransaction.transactionId));
      if (dupExisting.exists()) {
        await AuditLogger.logSystemEvent('OVERWRITE_DETECTED', {
          attemptedTransactionId: duplicatedTransaction.transactionId,
          existingData: dupExisting.data(),
          incomingData: duplicatedTransaction,
          source: 'duplicate_transaction',
          originalTransactionId: originalTransaction.transactionId,
        });
        throw new Error(
          `ID ${duplicatedTransaction.transactionId} already exists. The duplication was NOT saved. Please try again.`
        );
      }

      await setDoc(
        doc(db, "Sales", duplicatedTransaction.transactionId),
        duplicatedTransaction
      );

      // Log the duplication as a CREATE operation
      await AuditLogger.logSalesChange(
        'CREATE',
        duplicatedTransaction.transactionId,
        {}, // no before data for duplicated sales
        duplicatedTransaction,
        {
          source: 'duplicate_transaction',
          originalTransactionId: originalTransaction.transactionId,
          action: 'DUPLICATE'
        }
      );

      // Add to Educhecker
      await addToEduchecker(
        duplicatedTransaction.emailEdu,
        duplicatedTransaction.productName
      );

      // Update Clients database
      await updateClientData(duplicatedTransaction);

      setSuccess("Transaction duplicated successfully!");
      addSaleLocally(duplicatedTransaction); // Optimistic local update for instant UI
      refreshData(); // Refresh cached data after adding new transaction
      setDuplicatingTransactionId(null);
    } catch (error) {
      console.error("Error duplicating transaction:", error);
      setError(`Failed to duplicate transaction: ${error.message}`);
    }
  };

  // Function to handle renewal
  const handleRenew = async (originalTransaction) => {
    try {
      // Create a new transaction based on the original
      const newTrxId = await generateTransactionId();
      const renewedTransaction = {
        ...originalTransaction,
        transactionId: newTrxId,
        date: new Date().toISOString().split("T")[0], // Set to current date
        createdAt: new Date(),
        status: "Completed", // Set status to Completed for renewal
        // Original transaction ID is stored for reference
        renewedFrom: originalTransaction.transactionId,
      };

      // Overwrite detection guard
      const renewExisting = await getDoc(doc(db, "Sales", renewedTransaction.transactionId));
      if (renewExisting.exists()) {
        await AuditLogger.logSystemEvent('OVERWRITE_DETECTED', {
          attemptedTransactionId: renewedTransaction.transactionId,
          existingData: renewExisting.data(),
          incomingData: renewedTransaction,
          source: 'renew_transaction',
          originalTransactionId: originalTransaction.transactionId,
        });
        throw new Error(
          `ID ${renewedTransaction.transactionId} already exists. The renewal was NOT saved. Please try again.`
        );
      }

      await setDoc(
        doc(db, "Sales", renewedTransaction.transactionId),
        renewedTransaction
      );

      // Log the renewal as a CREATE operation
      await AuditLogger.logSalesChange(
        'CREATE',
        renewedTransaction.transactionId,
        {}, // no before data for renewed sales
        renewedTransaction,
        {
          source: 'renew_transaction',
          originalTransactionId: originalTransaction.transactionId,
          action: 'RENEW'
        }
      );

      // Add to Educhecker
      await addToEduchecker(
        renewedTransaction.emailEdu,
        renewedTransaction.productName
      );

      // Update the original transaction to mark it as renewed
      await updateDoc(doc(db, "Sales", originalTransaction.transactionId), {
        renewedTo: renewedTransaction.transactionId,
        renewedAt: new Date(),
      });

      // Update Clients database
      await updateClientData(renewedTransaction);

      setSuccess("Transaction renewed successfully!");
      addSaleLocally(renewedTransaction); // Optimistic local update for instant UI
      refreshData(); // Refresh cached data after adding new transaction
      setRenewingTransactionId(null);
    } catch (error) {
      console.error("Error renewing transaction:", error);
      setError(`Failed to renew transaction: ${error.message}`);
    }
  };

  // Add these states with your existing state declarations
  const [transaction, setTransaction] = useState({
    transactionId: "",
    clientName: "",
    company: "createv", // Default company selection
    productName: "",
    price: "",
    amount: "1",
    emailEdu: "Sample@Sinaieng.edu.eg", // Set default value here
    clientEmail: "",
    notes: "",
    paymentMethod: "N/A",
    contact1: "",
    contact1Type: "Whatsapp",
    contact2: "",
    contact2Type: "Whatsapp",
    date: new Date().toISOString().split("T")[0],
    day: new Date().toLocaleDateString("en-US", { weekday: "long" }),
    status: "Completed",
    subscriptionPeriod: "12 months",
  });
  const [isDateGroupingEnabled, setIsDateGroupingEnabled] = useState(false);
  const [view, setView] = useState("list");
  // Using context data instead of local state for products and sales
  // const [products, setProducts] = useState([]);
  // const [sales, setSales] = useState([]);
  const products = productsData || [];
  const sales = useMemo(() => localSalesData || [], [localSalesData]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showUnknownDate, setShowUnknownDate] = useState(false);
  const [toasts, setToasts] = useState([]);
  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    return id;
  };

  // Function to remove a toast notification
  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };
  // Add new state for showing/hiding no renewal records
  const [showNoRenewalRecords, setShowNoRenewalRecords] = useState(true);

  // Add new states
  const [renewedTransactions, setRenewedTransactions] = useState(new Set());
  const [showRenewedRecords, setShowRenewedRecords] = useState(true);

  // Add handler for renewed toggle
  const handleRenewedToggle = async (transactionId) => {
    if (isSaving) return; // Prevent rapid clicking while saving

    console.log(`🔄 Toggling Renewed for transaction: ${transactionId}`);
    try {
      const add = !renewedTransactions.has(transactionId);
      setRenewedTransactions((prev) => {
        const newSet = new Set(prev);
        if (add) newSet.add(transactionId);
        else newSet.delete(transactionId);
        return newSet;
      });
      saveTransactionStates("renewedTransactions", transactionId, add);
    } catch (error) {
      console.error("❌ Error in handleRenewedToggle:", error);
      addToast(`Failed to toggle Renewed: ${error.message}`, "error");
    }
  };

  // Add these state handlers for debugging
  const [isSaving, setIsSaving] = useState(false);

  // Add this function to fetch states from Firebase
  const fetchTransactionStates = async () => {
    console.log("🔍 Fetching transaction states from Firebase...");
    try {
      const statesRef = doc(db, "TransactionStates", "states");
      const statesDoc = await getDoc(statesRef);

      if (statesDoc.exists()) {
        const data = statesDoc.data();
        console.log("📥 Fetched states:", data);
        setNoRenewalTransactions(new Set(data.noRenewalTransactions || []));
        setRenewedTransactions(new Set(data.renewedTransactions || []));
      } else {
        console.log("⚠️ No states document exists yet");
      }
    } catch (error) {
      console.error("❌ Error fetching transaction states:", error);
    }
  };

  // Fetch state on component mount
  useEffect(() => {
    fetchTransactionStates();
  }, []);

  // Add this function to save states to Firebase
  // Adds/removes a single ID so other users' marks are never overwritten
  const saveTransactionStates = async (field, transactionId, add) => {
    console.log("💾 Saving transaction states to Firebase...");
    setIsSaving(true); // Disable checkboxes while saving

    // Create a toast ID for tracking this operation
    const toastId = addToast("Saving changes...", "info");

    try {
      const statesRef = doc(db, "TransactionStates", "states");
      const dataToSave = {
        [field]: add ? arrayUnion(transactionId) : arrayRemove(transactionId),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(statesRef, dataToSave, { merge: true });

      // Verify save and sync with marks made by other users
      const verifyDoc = await getDoc(statesRef);
      if (verifyDoc.exists()) {
        const data = verifyDoc.data();
        console.log("✅ Save verified:", data);
        setNoRenewalTransactions(new Set(data.noRenewalTransactions || []));
        setRenewedTransactions(new Set(data.renewedTransactions || []));

        // Update the toast to success
        removeToast(toastId);
        addToast("Changes saved successfully!", "success");
      } else {
        throw new Error("Save verification failed - document not found");
      }
    } catch (error) {
      console.error("❌ Error saving transaction states:", error);

      // Update the toast to error
      removeToast(toastId);
      addToast(`Failed to save changes: ${error.message}`, "error");
    } finally {
      setIsSaving(false); // Re-enable checkboxes
    }
  };

  // Add new state right here, after the existing useState declarations
  const tableHeaders = [
    { id: "quick_edit", label: "", width: 40 }, // New column for quick edit toggle
    { id: "transactionId", label: "ID", width: 110 },
    { id: "clientName", label: "Client Name", width: 180 },
    { id: "productName", label: "Product", width: 160 },
    { id: "price", label: "Price (EGP)", width: 115 },
    { id: "amount", label: "Amount", width: 30 },
    { id: "emailEdu", label: "Edu Email", width: 315 },
    { id: "clientEmail", label: "Client Email", width: 315 },
    { id: "paymentMethod", label: "Payment", width: 130 },
    { id: "contact1", label: "Contact 1", width: 800 },
    { id: "contact2", label: "Contact 2" },
    { id: "date", label: "Date" },
    { id: "status", label: "Status", width: 140 },
    { id: "subscriptionPeriod", label: "Period", width: 140 },
    { id: "expirationDays", label: "Expiration Days", width: 160 },
    { id: "notes", label: "Notes", width: 120 },
    { id: "actions", label: "Actions", width: 160 },
    { id: "actions2", label: "More", width: 120 }, // New column for Duplicate and future buttons
  ];
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Note: Removed modal-related state - now using separate edit page
  // const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // const [editingTransaction, setEditingTransaction] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "expirationDays",
    direction: "descending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);

  const [statistics, setStatistics] = useState({
    completed: 0,
    onHold: 0,
    processing: 0,
    refunded: 0,
  });

  // Add these new states at the top with your other state declarations
  const [noRenewalTransactions, setNoRenewalTransactions] = useState(new Set());

  // Add the handler function
  const handleNoRenewalToggle = async (transactionId) => {
    if (isSaving) return; // Prevent rapid clicking while saving

    console.log(`🔄 Toggling No Renewal for transaction: ${transactionId}`);
    try {
      const add = !noRenewalTransactions.has(transactionId);
      setNoRenewalTransactions((prev) => {
        const newSet = new Set(prev);
        if (add) newSet.add(transactionId);
        else newSet.delete(transactionId);
        return newSet;
      });
      saveTransactionStates("noRenewalTransactions", transactionId, add);
    } catch (error) {
      console.error("❌ Error in handleNoRenewalToggle:", error);
      addToast(`Failed to toggle No Renewal: ${error.message}`, "error");
    }
  };

  useEffect(() => {
    console.log("🚀 Component mounted - fetching initial states");
    fetchTransactionStates();
  }, []);

  // Handle success messages from edit page
  useEffect(() => {
    if (location.state?.message) {
      if (location.state.type === 'success') {
        setSuccess(location.state.message);
      } else if (location.state.type === 'error') {
        setError(location.state.message);
      }
      
      // Clear the message from location state to prevent it from showing again
      window.history.replaceState({}, document.title);
      
      // Auto-clear the message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
    }
  }, [location.state]);

  // Function to fetch renewal prices directly from Firestore
  const fetchRenewalPrices = async () => {
    try {
      console.log("🔄 Fetching renewal prices directly from Firestore...");

      // Force server fetch using cache-busting options
      const settingsRef = doc(db, "Settings", "renewalPrices");
      const pricesDoc = await getDoc(settingsRef, { source: "server" });

      if (pricesDoc.exists() && pricesDoc.data() && pricesDoc.data().prices) {
        const prices = pricesDoc.data().prices;
        console.log("✅ Renewal prices fetched successfully:", prices);

        // Update state with the fetched prices
        setRenewalPrices(prices);
        setRenewalPricesVersion(Date.now());

        return prices;
      } else {
        console.warn("⚠️ No renewal prices found in Firestore!");
        return {};
      }
    } catch (error) {
      console.error("❌ Error fetching renewal prices:", error);
      return {};
    }
  };

  useEffect(() => {
    // Data is already being fetched by DataContext
    // Only fetch renewal prices here
    fetchRenewalPrices();
  }, []);

  // Initialize Firestore ID counter on mount (only creates it if it doesn't exist yet)
  useEffect(() => {
    const initCounter = async () => {
      try {
        const counterRef = doc(db, "Settings", "idCounter");
        const counterSnap = await getDoc(counterRef);
        if (!counterSnap.exists()) {
          // First time: scan ALL sales in Firestore to find the true current maximum ID
          const snapshot = await getDocs(collection(db, "Sales"));
          let maxId = 0;
          snapshot.docs.forEach((d) => {
            const tid = d.data().transactionId || d.id;
            const match = tid.match(/TRX-(\d+)/i);
            if (match) maxId = Math.max(maxId, parseInt(match[1]));
          });
          await setDoc(counterRef, { lastId: maxId });
          console.log("✅ ID counter initialized at", maxId);
        }
      } catch (err) {
        console.error("❌ Failed to initialize ID counter:", err);
      }
    };
    initCounter();
  }, []);

  // Handle errors from DataContext
  useEffect(() => {
    if (dataError) {
      setError(`Data loading error: ${dataError}`);
    }
  }, [dataError]);

  useEffect(() => {
    filterAndSortSales();
    calculateStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localSalesData,
    searchTerm,
    statusFilter,
    productFilter,
    periodFilter,
    dateFrom,
    dateTo,
    sortConfig,
    isDateGroupingEnabled,
    showUnknownDate,
    showNoRenewalRecords,
    showRenewedRecords,
    noRenewalTransactions,
    renewedTransactions,
  ]);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState("");
  const [visibleColumns] = useState({
    quick_edit: true, // New column for quick edit toggle
    transactionId: true,
    clientName: true,
    productName: true,
    price: true,
    amount: true,
    emailEdu: true,
    clientEmail: true,
    paymentMethod: true,
    contact1: true,
    contact2: true,
    date: true,
    status: true,
    subscriptionPeriod: true,
    expirationDays: true,
    notes: true,
    actions: true,
    actions2: true,
  });
  
  const calculateStatistics = () => {
    const stats = {
      completed: 0,
      onHold: 0,
      processing: 0,
      refunded: 0,
    };

    localSalesData.forEach((sale) => {
      switch (sale.status) {
        case "Completed":
          stats.completed++;
          break;
        case "OnHold":
          stats.onHold++;
          break;
        case "Processing":
          stats.processing++;
          break;
        case "Refunded":
          stats.refunded++;
          break;
        default:
          break;
      }
    });

    setStatistics(stats);
  };

  const filterAndSortSales = () => {
    let filtered = localSalesData;

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((sale) =>
        Object.values(sale).some(
          (value) =>
            value &&
            typeof value === "string" &&
            value.toLowerCase().includes(lowerSearchTerm)
        )
      );
    }

    // Apply status filter
    if (statusFilter !== "All") {
      filtered = filtered.filter((sale) => sale.status === statusFilter);
    }

    // Apply product filter
    if (productFilter !== "All") {
      filtered = filtered.filter((sale) => sale.productName === productFilter);
    }

    // Apply period filter
    if (periodFilter !== "All") {
      filtered = filtered.filter((sale) => sale.subscriptionPeriod === periodFilter);
    }

    // Apply date range filter
    if (dateFrom) {
      filtered = filtered.filter((sale) => sale.date && sale.date !== "unknown" && sale.date >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter((sale) => sale.date && sale.date !== "unknown" && sale.date <= dateTo);
    }

    // Apply unknown date filter
    if (!showUnknownDate) {
      filtered = filtered.filter((sale) => sale.date !== "unknown");
    }

    // Apply no renewal filter
    if (!showNoRenewalRecords) {
      filtered = filtered.filter(
        (sale) => !noRenewalTransactions.has(sale.transactionId)
      );
    }

    // Apply renewed filter (manually marked, or renewed via the Renew button)
    if (!showRenewedRecords) {
      filtered = filtered.filter(
        (sale) => !renewedTransactions.has(sale.transactionId) && !sale.renewedTo
      );
    }

    // Sort the data
    const sortedSales = [...filtered].sort((a, b) => {
      if (sortConfig.key === "expirationDays") {
        const aRaw = calculateExpirationDays(a.date, a.subscriptionPeriod);
        const bRaw = calculateExpirationDays(b.date, b.subscriptionPeriod);
        // Convert non-numeric values to sortable numbers:
        // "∞" (one-time) goes to the end, non-numeric ("N/A", etc.) goes after that
        const toNum = (v) => {
          if (typeof v === "number") return v;
          if (v === "∞") return Infinity;
          return sortConfig.direction === "ascending" ? Infinity + 1 : -(Infinity + 1);
        };
        const aValue = toNum(aRaw);
        const bValue = toNum(bRaw);
        // Handle both being non-finite the same way
        if (!isFinite(aValue) && !isFinite(bValue)) return 0;
        if (!isFinite(aValue)) return 1;
        if (!isFinite(bValue)) return -1;
        return sortConfig.direction === "ascending"
          ? aValue - bValue
          : bValue - aValue;
      } else if (sortConfig.key === "date") {
        // Handle unknown dates in sorting
        if (a.date === "unknown" && b.date !== "unknown") {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a.date !== "unknown" && b.date === "unknown") {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        if (a.date === "unknown" && b.date === "unknown") {
          return 0;
        }

        return sortConfig.direction === "ascending"
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      } else {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      }
    });

    // Apply date grouping if enabled
    const finalSales = isDateGroupingEnabled
      ? groupSalesByDate(sortedSales)
      : sortedSales;
    setFilteredSales(finalSales);
  };
  const SavingIndicator = () => {
    if (!isSaving) return null;

    return (
      <div
        style={{
          position: "fixed",
          bottom: "84px",
          right: "24px",
          backgroundColor: "#f0f9ff",
          padding: "8px 16px",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          zIndex: 9998,
        }}
      >
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            border: "2px solid #3b82f6",
            borderTopColor: "transparent",
            animation: "spin 1s linear infinite",
          }}
        />
        <span>Saving changes...</span>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  };
  // Updated function to store product lists per email
  const addToEduchecker = useCallback(async (eduMail, productName) => {
    if (!eduMail || !productName) return;

    try {
      // Normalize the email to ensure consistent storage
      const normalizedEmail = eduMail.toLowerCase().trim();

      // Use the normalized email as document ID
      const educheckerRef = doc(db, "Educhecker", normalizedEmail);

      // Check if document already exists
      const docSnapshot = await getDoc(educheckerRef);

      if (docSnapshot.exists()) {
        // Document exists, update the productNames array
        const data = docSnapshot.data();
        const productNames = data.productNames || [];

        // Only add product if it's not already in the array
        if (!productNames.includes(productName)) {
          await updateDoc(educheckerRef, {
            productNames: [...productNames, productName],
            eduMail: normalizedEmail,
            lastUpdated: new Date(),
          });
        }
      } else {
        // Document doesn't exist, create new one with productNames array
        await setDoc(educheckerRef, {
          eduMail: normalizedEmail,
          productNames: [productName],
          createdAt: new Date(),
        });
      }

      console.log("Added to Educhecker:", normalizedEmail, productName);
    } catch (error) {
      console.error("Error adding to Educhecker:", error);
    }
  }, []);

  // Move updateClientData before handleEdit to fix hoisting issue
  const updateClientData = useCallback(async (saleData) => {
    const clientKey =
      saleData.contact1 || saleData.contact2 || saleData.clientEmail;
    if (!clientKey) {
      console.log(
        "Skipping client update due to missing contact info:",
        saleData
      );
      return;
    }

    try {
      const clientRef = doc(db, "Clients", clientKey);
      const clientDoc = await getDoc(clientRef);

      if (clientDoc.exists()) {
        // Update existing client
        const existingData = clientDoc.data();
        await updateDoc(clientRef, {
          clientName: saleData.clientName,
          contact1: saleData.contact1 || existingData.contact1,
          contact2: saleData.contact2 || existingData.contact2,
          clientEmail: saleData.clientEmail || existingData.clientEmail,
          products: [
            ...new Set([...existingData.products, saleData.productName]),
          ],
        });
      } else {
        // Create new client
        await setDoc(clientRef, {
          clientName: saleData.clientName,
          contact1: saleData.contact1,
          contact2: saleData.contact2,
          clientEmail: saleData.clientEmail,
          products: [saleData.productName],
        });
      }
      console.log("Client data updated successfully");
    } catch (error) {
      console.error("Error updating client data:", error);
    }
  }, []);

  const handleEdit = useCallback(async (sale) => {
    console.log('🎯 HANDLE EDIT CALLED - Scroll position:', window.scrollY);
    
    // If it's a direct call from the quick edit form (sale has _isQuickEdit flag)
    if (sale && sale._isQuickEdit) {
      console.log('🔧 Quick edit branch');
      try {
        // Remove the _isQuickEdit flag before saving
        const { _isQuickEdit, ...saleData } = sale;

        // Get original data for audit logging
        const originalSale = sales.find(s => s.transactionId === saleData.transactionId);
        
        // Detect changes
        const changes = detectChanges(originalSale, saleData);

        // Update locally first for immediate UI feedback
        updateSaleLocally(saleData);

        // Save the edited transaction to backend
        await setDoc(doc(db, "Sales", saleData.transactionId), saleData);
        
        // Log the changes if there are any significant changes
        if (changes.hasChanges) {
          await AuditLogger.logSalesChange(
            'UPDATE',
            saleData.transactionId,
            originalSale,
            saleData,
            {
              source: 'quick_edit',
              fieldsChanged: changes.fieldsChanged,
              changeCount: changes.statistics.changedFields
            }
          );
        }

        // Update Educhecker and Clients database
        await addToEduchecker(saleData.emailEdu, saleData.productName);
        await updateClientData(saleData);

        setSuccess("Transaction updated successfully!");
        
        // Force cache invalidation to ensure real-time sync across tabs
        if (window.cacheManager) {
          window.cacheManager.invalidateAll();
        }
        
        // Note: We don't call refreshData() here since we've already updated locally
        // The real-time listener should pick up the change and sync across tabs
        return true;
      } catch (error) {
        console.error("Error updating transaction:", error);
        
        // If backend update fails, revert the local change
        refreshData();
        
        setError(`Failed to update transaction: ${error.message}`);
        return false;
      }
    } else {
      // Full edit: load transaction from Firestore and show the SalesForm inline
      try {
        const transactionRef = doc(db, 'Sales', sale.transactionId);
        const snap = await getDoc(transactionRef);
        if (snap.exists()) {
          const data = snap.data();
          setEditingTransaction({
            transactionId: data.transactionId || sale.transactionId,
            clientName: data.clientName || '',
            company: data.company || 'createv',
            productName: data.productName || '',
            productVariant: data.productVariant || '',
            price: data.price !== undefined ? String(data.price) : '',
            amount: data.amount !== undefined ? String(data.amount) : '1',
            emailEdu: data.emailEdu || '',
            clientEmail: data.clientEmail || '',
            paymentMethod: data.paymentMethod || 'N/A',
            contact1: data.contact1 || '',
            contact1Type: data.contact1Type || 'Whatsapp',
            contact2: data.contact2 || '',
            contact2Type: data.contact2Type || 'Whatsapp',
            date: data.date || new Date().toISOString().split('T')[0],
            day: data.day || '',
            status: data.status || 'Completed',
            subscriptionPeriod: data.subscriptionPeriod || '12 months',
            notes: data.notes || '',
          });
          setView('edit');
        } else {
          setError('Transaction not found');
        }
      } catch (err) {
        setError(`Failed to load transaction: ${err.message}`);
      }
      return true;
    }
  }, [addToEduchecker, updateClientData, updateSaleLocally, refreshData, setSuccess, setError, sales]); // Updated dependencies

  const handleDelete = useCallback(async (transactionId) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      try {
        // Get the transaction data before deleting for audit log
        const transactionRef = doc(db, "Sales", transactionId);
        const transactionSnap = await getDoc(transactionRef);
        const transactionData = transactionSnap.exists() ? transactionSnap.data() : null;
        
        // Delete locally first for immediate UI feedback
        deleteSaleLocally(transactionId);

        // Delete from backend
        await deleteDoc(transactionRef);
        
        // Log the deletion
        if (transactionData) {
          await AuditLogger.logSalesChange(
            'DELETE',
            transactionId,
            transactionData,
            {},
            {
              source: 'sales_list',
              deletedBy: 'user_action'
            }
          );
        }
        
        console.log("Transaction deleted successfully");
        setSuccess("Transaction deleted successfully!");
        
        // Note: We don't call refreshData() here since we've already updated locally
        // The next time data is refreshed, it will sync with the backend
      } catch (error) {
        console.error("Error deleting transaction:", error);
        
        // If backend deletion fails, refresh to restore the deleted item
        refreshData();
        
        setError(
          `Failed to delete transaction: ${error.message || "Unknown error"}`
        );
      }
    }
  }, [deleteSaleLocally, refreshData, setSuccess, setError]); // Updated dependencies

  const calculateExpirationDays = (date, period) => {
    if (!date || !period) return "N/A";

    // Handle unknown date
    if (date === "unknown") return "N/A";

    const startDate = new Date(date);
    if (isNaN(startDate.getTime())) return "Invalid Date";

    // Handle one-time purchase
    if (period === "one-time") return "∞";

    const monthsMatch = period.match(/(\d+)\s*month/);
    if (!monthsMatch) return "Invalid Period";

    const months = parseInt(monthsMatch[1]);
    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + months);

    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return negative days for expired products
    return diffDays;
  };

  // Atomic ID generator — uses a Firestore server-side counter so it is safe
  // across multiple devices/browsers simultaneously and works in 5-day data mode.
  const generateTransactionId = async () => {
    const counterRef = doc(db, "Settings", "idCounter");
    let newId;
    await runTransaction(db, async (t) => {
      const counterSnap = await t.get(counterRef);
      const currentId = counterSnap.exists() ? (counterSnap.data().lastId || 0) : 0;
      newId = currentId + 1;
      t.set(counterRef, { lastId: newId });
    });
    return `TRX-${String(newId).padStart(3, "0")}`;
  };

  const openNoteModal = (note) => {
    setSelectedNote(note);
    setNoteModalOpen(true);
  };

  const closeNoteModal = () => {
    setNoteModalOpen(false);
    setSelectedNote("");
  };

  const exportAllData = async () => {
    try {
      setError(null);

      const salesQuery = query(
        collection(db, "Sales"),
        orderBy("date", "desc")
      );
      const querySnapshot = await getDocs(salesQuery);
      const allSales = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by expiration days ascending (soonest expiring first, expired first)
      allSales.sort((a, b) => {
        const aDays = calculateExpirationDays(a.date, a.subscriptionPeriod);
        const bDays = calculateExpirationDays(b.date, b.subscriptionPeriod);
        // Put N/A / Invalid at the end
        const aNum = typeof aDays === "number" ? aDays : Infinity;
        const bNum = typeof bDays === "number" ? bDays : Infinity;
        // ∞ (one-time) goes to the end
        if (aDays === "∞" && bDays === "∞") return 0;
        if (aDays === "∞") return 1;
        if (bDays === "∞") return -1;
        return aNum - bNum;
      });

      const csvData = allSales.map((sale) => {
        const expDays = calculateExpirationDays(sale.date, sale.subscriptionPeriod);
        const isRenewed = renewedTransactions.has(sale.transactionId) || !!sale.renewedTo;
        const isNoRenewal = noRenewalTransactions.has(sale.transactionId);
        const renewedStatus = isRenewed ? "Renewed" : isNoRenewal ? "Not Renewed" : "";
        return {
          transactionId: sale.transactionId,
          clientName: sale.clientName,
          productName: sale.productName,
          price: sale.price,
          amount: sale.amount,
          emailEdu: sale.emailEdu,
          clientEmail: sale.clientEmail,
          paymentMethod: sale.paymentMethod,
          date: sale.date,
          contact1: sale.contact1,
          contact1Type: sale.contact1Type,
          contact2: sale.contact2,
          contact2Type: sale.contact2Type,
          status: sale.status,
          notes: sale.notes,
          subscriptionPeriod: sale.subscriptionPeriod,
          expirationDays: expDays,
          renewed: renewedStatus,
        };
      });

      const csv = Papa.unparse(csvData, {
        quotes: true,
        header: true,
        encoding: "UTF-8",
      });

      // Log the export action
      await AuditLogger.logUserAction('EXPORT', {
        operation: 'sales_csv_export',
        recordCount: allSales.length,
        exportFormat: 'CSV',
        filename: 'all_sales_data.csv',
        fieldsExported: Object.keys(csvData[0] || {}),
        exportSize: csv.length
      });

      const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "all_sales_data.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data: ", error);
      
      // Log the export error
      await AuditLogger.logSystemEvent('EXPORT_ERROR', {
        operation: 'sales_csv_export',
        error: error.message,
        errorStack: error.stack
      });
      
      setError(`Failed to export data: ${error.message}`);
    }
  };

  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "clamp(12px, 2vw, 24px)",
      backgroundColor: "#f8fafc",
    },
    header: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#1e293b",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "16px",
      marginBottom: "24px",
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginTop: "20px",
    },
    pageButton: {
      margin: "0 5px",
      padding: "5px 10px",
      border: "none",
      backgroundColor: "#4CAF50",
      color: "white",
      cursor: "pointer",
    },
    searchAndFilterContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      marginBottom: "10px",
      "@media (min-width: 768px)": {
        flexDirection: "row",
        alignItems: "center",
      },
    },
    filterContainer: {
      flex: 1,
      marginBottom: "8px",
    },
    searchInput: {
      width: "100%",
      padding: "12px 16px",
      fontSize: "16px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      transition: "border-color 0.3s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      "&:focus": {
        borderColor: "#4CAF50",
        outline: "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      },
    },
    statusFilterContainer: {
      width: "100%",
      "@media (min-width: 768px)": {
        width: "200px",
      },
    },
    statusSelect: {
      width: "100%",
      padding: "12px 16px",
      fontSize: "16px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      backgroundColor: "#fff",
      cursor: "pointer",
      "&:focus": {
        borderColor: "#4CAF50",
        outline: "none",
      },
    },
  };

  // Get current sales - MEMOIZED to prevent unnecessary re-renders
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  
  // Memoize currentSales to prevent new array creation on every render
  const currentSales = useMemo(() => {
    const sliced = filteredSales.slice(indexOfFirstItem, indexOfLastItem);
    console.log('🔄 Sales.js: currentSales recalculated', {
      filteredSalesLength: filteredSales.length,
      indexOfFirstItem,
      indexOfLastItem,
      currentSalesLength: sliced.length,
      timestamp: Date.now()
    });
    return sliced;
  }, [filteredSales, indexOfFirstItem, indexOfLastItem]);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Memoized handlers for SalesTable to prevent re-renders
  const handleDuplicateCallback = useCallback((transaction) =>
    setDuplicatingTransactionId(transaction.transactionId), []);
  
  const handleRenewCallback = useCallback((transaction) =>
    setRenewingTransactionId(transaction.transactionId), []);

  // Bulk update handler with local-first updates
  const handleBulkUpdate = useCallback(async (updates, transactionIds) => {
    try {
      // Update locally first for immediate UI feedback
      updateMultipleSalesLocally(updates, transactionIds);

      // Update backend
      const updatePromises = transactionIds.map(async (transactionId) => {
        const transactionRef = doc(db, 'Sales', transactionId);
        
        // Get current data for audit logging
        const currentDoc = await getDoc(transactionRef);
        const beforeData = currentDoc.exists() ? currentDoc.data() : {};
        const afterData = { ...beforeData, ...updates, updatedAt: new Date() };
        
        // Log the individual update as part of bulk operation
        await AuditLogger.logSalesChange('UPDATE', transactionId, beforeData, afterData, {
          operationType: 'bulk_update',
          batchSize: transactionIds.length,
          batchId: `bulk_${Date.now()}`
        });
        
        return updateDoc(transactionRef, {
          ...updates,
          updatedAt: new Date()
        });
      });

      await Promise.all(updatePromises);
      
      // Log the bulk operation summary
      await AuditLogger.logUserAction('BULK_UPDATE', {
        operation: 'sales_bulk_update',
        recordCount: transactionIds.length,
        updatedFields: Object.keys(updates),
        transactionIds: transactionIds,
        batchId: `bulk_${Date.now()}`
      });
      
      setSuccess(`Successfully updated ${transactionIds.length} records!`);
      
      // Force cache invalidation to ensure real-time sync across tabs
      if (window.cacheManager) {
        window.cacheManager.invalidateAll();
      }
      
      // Note: We don't call refreshData() here since we've already updated locally
      // The real-time listener should pick up the changes and sync across tabs
      return true;
    } catch (error) {
      console.error('Bulk update error:', error);
      
      // Log the error
      await AuditLogger.logSystemEvent('BULK_UPDATE_ERROR', {
        operation: 'sales_bulk_update',
        recordCount: transactionIds.length,
        error: error.message,
        transactionIds: transactionIds
      });
      
      // If backend update fails, refresh to restore the correct state
      refreshData();
      
      setError(`Failed to update records: ${error.message}`);
      return false;
    }
  }, [updateMultipleSalesLocally, refreshData, setSuccess, setError]);

  const groupSalesByDate = (sales) => {
    if (!isDateGroupingEnabled) return sales;

    const groupedSales = sales.reduce((acc, sale) => {
      const date = sale.date;
      if (!acc[date]) {
        acc[date] = {
          date: date,
          transactionId: `Group-${date}`,
          clientName: `${sales.filter((s) => s.date === date).length} orders`,
          price: sales
            .filter((s) => s.date === date)
            .reduce((sum, s) => sum + parseFloat(s.price || 0), 0),
          isGroupedRow: true,
          originalSales: sales.filter((s) => s.date === date),
        };
      }
      return acc;
    }, {});

    return Object.values(groupedSales);
  };

  // Note: Removed scroll preservation code as we now use modal-based editing
  // which doesn't require view transitions or scroll management

  return (
    <div style={styles.container}>
      {/* <ScrollDebugger /> */}
      <h2 style={styles.header}>
        <DollarSign size={24} />
        Sales Management
      </h2>
      <div style={styles.statGrid}>
        <StatCard
          title="Completed"
          value={statistics.completed}
          icon={<CheckCircle />}
          color="#4ade80"
        />
        <StatCard
          title="Processing"
          value={statistics.processing}
          icon={<RefreshCcw />}
          color="#60a5fa"
        />
        <StatCard
          title="On Hold"
          value={statistics.onHold}
          icon={<Clock />}
          color="#fbbf24"
        />
        <StatCard
          title="Refunded"
          value={statistics.refunded}
          icon={<AlertCircle />}
          color="#f87171"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <button
          onClick={() => {
            setView("list");
          }}
          className="flex items-center justify-center gap-2 px-4 py-4 bg-blue-400 text-white rounded-lg hover:bg-blue-700 transition-colors text-base font-medium"
        >
          <FileText size={20} />
          <span>View Sales List</span>
        </button>

        <button
          onClick={() => {
            setView("add");
            setTransaction({
              transactionId: "",
              clientName: "",
              productName: "",
              price: "",
              amount: "1",
              emailEdu: "Sample@Sinaieng.edu.eg",
              clientEmail: "",
              notes: "",
              paymentMethod: "N/A",
              contact1: "",
              contact1Type: "Whatsapp",
              contact2: "",
              contact2Type: "Whatsapp",
              date: new Date().toISOString().split("T")[0],
              day: new Date().toLocaleDateString("en-US", { weekday: "long" }),
              status: "OnHold",
              subscriptionPeriod: "12 months",
            });
          }}
          className="flex items-center justify-center gap-2 px-4 py-4 bg-green-400 text-white rounded-lg hover:bg-green-700 transition-colors text-base font-medium"
        >
          <Plus size={20} />
          <span>Add New Sale</span>
        </button>

        <button
          onClick={exportAllData}
          className="flex items-center justify-center gap-2 px-4 py-4 bg-indigo-400 text-white rounded-lg hover:bg-indigo-700 transition-colors text-base font-medium"
        >
          <Download size={20} />
          <span>Export All Data</span>
        </button>

        <button
          onClick={() => setIsDateGroupingEnabled(!isDateGroupingEnabled)}
          className={`flex items-center justify-center gap-2 px-4 py-4 rounded-lg transition-colors text-base font-medium ${
            isDateGroupingEnabled
              ? "bg-yellow-600 hover:bg-yellow-700"
              : "bg-gray-600 hover:bg-gray-700"
          } text-white`}
        >
          <Calendar size={20} />
          <span>{isDateGroupingEnabled ? "Disable" : "Enable"} Grouping</span>
        </button>
      </div>



      {error && (
        <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
      )}
      {success && (
        <div style={{ color: "green", marginBottom: "10px" }}>{success}</div>
      )}

      {view === "list" && (
        <div>
          <h3>Sales List</h3>

          {/* Search + Advanced Filter button row */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Search in all columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            {/* Advanced Filter button */}
            <button
              onClick={() => setShowFilterDialog(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 16px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: (statusFilter !== "All" || productFilter !== "All" || periodFilter !== "All" || dateFrom || dateTo) ? "#3b82f6" : "#fff",
                color: (statusFilter !== "All" || productFilter !== "All" || periodFilter !== "All" || dateFrom || dateTo) ? "#fff" : "#374151",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              <Filter size={15} />
              Filters
              {/* Active filter count badge */}
              {(() => {
                const count = [statusFilter !== "All", productFilter !== "All", periodFilter !== "All", !!dateFrom, !!dateTo].filter(Boolean).length;
                return count > 0 ? (
                  <span style={{ background: "#fff", color: "#3b82f6", borderRadius: "999px", padding: "0 6px", fontSize: "12px", fontWeight: 700 }}>{count}</span>
                ) : null;
              })()}
            </button>
          </div>

          {/* Active filter pills */}
          {(statusFilter !== "All" || productFilter !== "All" || periodFilter !== "All" || dateFrom || dateTo) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
              {statusFilter !== "All" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#dbeafe", color: "#1d4ed8", borderRadius: "999px", padding: "2px 10px", fontSize: "12px" }}>
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter("All")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}><X size={12} /></button>
                </span>
              )}
              {productFilter !== "All" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#dcfce7", color: "#15803d", borderRadius: "999px", padding: "2px 10px", fontSize: "12px" }}>
                  Product: {productFilter}
                  <button onClick={() => setProductFilter("All")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}><X size={12} /></button>
                </span>
              )}
              {periodFilter !== "All" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#fef9c3", color: "#854d0e", borderRadius: "999px", padding: "2px 10px", fontSize: "12px" }}>
                  Period: {periodFilter}
                  <button onClick={() => setPeriodFilter("All")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}><X size={12} /></button>
                </span>
              )}
              {(dateFrom || dateTo) && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#f3e8ff", color: "#7e22ce", borderRadius: "999px", padding: "2px 10px", fontSize: "12px" }}>
                  Date: {dateFrom || "…"} → {dateTo || "…"}
                  <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1 }}><X size={12} /></button>
                </span>
              )}
              <button
                onClick={() => { setStatusFilter("All"); setProductFilter("All"); setPeriodFilter("All"); setDateFrom(""); setDateTo(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "12px", textDecoration: "underline", padding: "2px 4px" }}
              >
                Clear all
              </button>
            </div>
          )}

          {/* Advanced Filter Dialog */}
          {showFilterDialog && (
            <div
              onClick={(e) => { if (e.target === e.currentTarget) setShowFilterDialog(false); }}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "420px", maxWidth: "95vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Advanced Filters</h3>
                  <button onClick={() => setShowFilterDialog(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#6b7280" }}><X size={18} /></button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {/* Status */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Status</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...styles.statusSelect, fontSize: "14px", padding: "8px 12px" }}>
                      <option value="All">All Statuses</option>
                      <option value="OnHold">OnHold</option>
                      <option value="Processing">Processing</option>
                      <option value="Completed">Completed</option>
                      <option value="Refunded">Refunded</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>

                  {/* Product */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Product</label>
                    <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)} style={{ ...styles.statusSelect, fontSize: "14px", padding: "8px 12px" }}>
                      <option value="All">All Products</option>
                      {[...new Set(localSalesData.map((s) => s.productName).filter(Boolean))].sort().map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Period */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Subscription Period</label>
                    <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} style={{ ...styles.statusSelect, fontSize: "14px", padding: "8px 12px" }}>
                      <option value="All">All Periods</option>
                      {[...new Set(localSalesData.map((s) => s.subscriptionPeriod).filter(Boolean))]
                        .sort((a, b) => {
                          if (a === "one-time") return 1;
                          if (b === "one-time") return -1;
                          return (parseInt(a) || 0) - (parseInt(b) || 0);
                        })
                        .map((period) => (
                          <option key={period} value={period}>{period}</option>
                        ))}
                    </select>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Date Range</label>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        style={{ flex: 1, padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" }}
                      />
                      <span style={{ color: "#9ca3af", fontSize: "13px" }}>to</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        style={{ flex: 1, padding: "8px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Dialog actions */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "24px" }}>
                  <button
                    onClick={() => { setStatusFilter("All"); setProductFilter("All"); setPeriodFilter("All"); setDateFrom(""); setDateTo(""); }}
                    style={{ padding: "8px 16px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb", cursor: "pointer", fontSize: "14px", color: "#374151" }}
                  >
                    Reset all
                  </button>
                  <button
                    onClick={() => setShowFilterDialog(false)}
                    style={{ padding: "8px 20px", border: "none", borderRadius: "8px", background: "#3b82f6", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f0f9ff",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid #bae6fd",
              }}
            >
              <input
                type="checkbox"
                id="showUnknownDate"
                checked={showUnknownDate}
                onChange={(e) => setShowUnknownDate(e.target.checked)}
                style={{ marginRight: "8px", width: "16px", height: "16px" }}
              />
              <label
                htmlFor="showUnknownDate"
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Show Unknown Date Records
              </label>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff7ed",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid #fcd34d",
              }}
            >
              <input
                type="checkbox"
                id="showNoRenewalRecords"
                checked={showNoRenewalRecords}
                onChange={(e) => setShowNoRenewalRecords(e.target.checked)}
                style={{ marginRight: "8px", width: "16px", height: "16px" }}
              />
              <label
                htmlFor="showNoRenewalRecords"
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Show No Renewal Records
              </label>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f0fdf4",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid #86efac",
              }}
            >
              <input
                type="checkbox"
                id="showRenewedRecords"
                checked={showRenewedRecords}
                onChange={(e) => setShowRenewedRecords(e.target.checked)}
                style={{ marginRight: "8px", width: "16px", height: "16px" }}
              />
              <label
                htmlFor="showRenewedRecords"
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Show Renewed Records
              </label>
            </div>
          </div>

          <SalesTable
            data={currentSales}
            columns={tableHeaders}
            visibleColumns={visibleColumns}
            sortDirection={sortDirection}
            toggleSort={toggleSort}
            calculateExpirationDays={calculateExpirationDays}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleDuplicate={handleDuplicateCallback}
            handleRenew={handleRenewCallback}
            openNoteModal={openNoteModal}
            noRenewalTransactions={noRenewalTransactions}
            onNoRenewalToggle={handleNoRenewalToggle}
            showNoRenewalRecords={showNoRenewalRecords}
            renewedTransactions={renewedTransactions}
            onRenewedToggle={handleRenewedToggle}
            showRenewedRecords={showRenewedRecords}
            isSaving={isSaving}
            renewalPrices={renewalPrices} // Pass renewal prices directly
            renewalPricesVersion={renewalPricesVersion} // Pass version number for tracking updates
            products={products} // Pass products for fallback pricing
            refreshData={refreshData} // Pass refresh function for manual refresh
            handleBulkUpdate={handleBulkUpdate} // Pass bulk update handler for immediate updates
          />

          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isFullDataLoaded ? "#eff6ff" : "#f8fafc",
            padding: "12px 20px",
            borderRadius: "8px",
            border: isFullDataLoaded ? "1px solid #93c5fd" : "1px solid #e2e8f0",
            marginTop: "16px",
            gap: "10px",
          }}>
            <input
              type="checkbox"
              id="loadAllData"
              checked={isFullDataLoaded}
              onChange={(e) => {
                if (e.target.checked) {
                  loadAllData();
                } else {
                  unloadAllData();
                }
              }}
              style={{ width: "18px", height: "18px", cursor: "pointer" }}
            />
            <label htmlFor="loadAllData" style={{ cursor: "pointer", userSelect: "none", fontWeight: 500 }}>
              Load all database records {isFullDataLoaded ? "(active — auto-reverts to last 5 days in ~1 hour)" : "(default: last 5 days only)"}
            </label>
          </div>

          <div style={styles.pagination}>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              style={styles.pageButton}
            >
              Previous
            </button>
            <span>{currentPage}</span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={indexOfLastItem >= filteredSales.length}
              style={styles.pageButton}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {view === "edit" && editingTransaction && (
        <SalesForm
          transaction={editingTransaction}
          setTransaction={setEditingTransaction}
          isEditing={true}
          editingId={editingTransaction.transactionId}
          products={products}
          onSubmitSuccess={(shouldResetForm, savedData) => {
            setView("list");
            setEditingTransaction(null);
            if (savedData) updateSaleLocally(savedData);
            refreshData();
          }}
          generateTransactionId={() => editingTransaction.transactionId}
          fetchSales={refreshData}
        />
      )}

      {view === "add" && (
        <SalesForm
          transaction={transaction}
          setTransaction={setTransaction}
          isEditing={false}
          editingId={null}
          products={products}
          onSubmitSuccess={(shouldResetForm = true, savedData) => {
            // For new transactions, always switch back to list view
            if (shouldResetForm) {
              setView("list");
              // Reset transaction to default values with createv as default company
              setTransaction({
                transactionId: "",
                clientName: "",
                company: "createv", // Default company selection
                productName: "",
                price: "",
                amount: "1",
                emailEdu: "Sample@Sinaieng.edu.eg",
                clientEmail: "",
                notes: "",
                paymentMethod: "N/A",
                contact1: "",
                contact1Type: "Whatsapp",
                contact2: "",
                contact2Type: "Whatsapp",
                date: new Date().toISOString().split("T")[0],
                day: new Date().toLocaleDateString("en-US", { weekday: "long" }),
                status: "Completed",
                subscriptionPeriod: "12 months",
              });
            }
            if (savedData) addSaleLocally(savedData); // Optimistic local update for instant UI
            refreshData(); // Refresh cached data after form submission
          }}
          generateTransactionId={generateTransactionId}
          fetchSales={refreshData}
        />
      )}

      {/* Note Modal */}
      <NoteModal
        note={selectedNote}
        isOpen={noteModalOpen}
        onClose={closeNoteModal}
      />

      {/* Duplicate Confirmation Modal */}
      <ActionModal
        isOpen={Boolean(duplicatingTransactionId)}
        title="Confirm Duplication"
        message="Are you sure you want to duplicate this transaction? A new transaction will be created with today's date."
        confirmText="Confirm Duplication"
        actionType="duplicate"
        onConfirm={() => {
          const transaction = sales.find(
            (s) => s.transactionId === duplicatingTransactionId
          );
          if (transaction) {
            handleDuplicate(transaction);
          }
        }}
        onCancel={() => setDuplicatingTransactionId(null)}
        data={null}
      />

      {/* Renew Confirmation Modal */}
      <ActionModal
        isOpen={Boolean(renewingTransactionId)}
        title="Confirm Renewal"
        message="Are you sure you want to renew this transaction? A new transaction will be created with today's date and the current one will be marked as renewed."
        confirmText="Confirm Renewal"
        actionType="renew"
        onConfirm={() => {
          const transaction = sales.find(
            (s) => s.transactionId === renewingTransactionId
          );
          if (transaction) {
            handleRenew(transaction);
          }
        }}
        onCancel={() => setRenewingTransactionId(null)}
        data={null}
      />

      {/* Note: EditForm modal removed - now using separate edit page at /sales/edit/:transactionId */}
      
      <SavingIndicator />
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

export default Sales;
